import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { CommonModule, DecimalPipe } from "@angular/common";
import { MatIcon } from "@angular/material/icon";
import { MatTooltip } from "@angular/material/tooltip";
import { SharedModule } from "../../../../shared/shared.module";
import { DialogHeaderExportComponent } from "../../../../shared/modules/dialog/dialog-header-export/dialog-header-export.component";
import { RadioCardComponent } from "../../../../shared/components/radio-card/radio-card.component";
import { LotCardComponent } from "../../../../shared/components/lot-card/lot-card.component";
import { ChipsToggleComponent } from "../../../../shared/modules/chips-toggle/chips-toggle.component";
import { WidgetHelperService } from "../../../dashboard/services/widget-helper.service";
import { exportVenteEvolutionExcel } from "./stats-vente-evolution-excel.utils";
import { NcAsideBlockComponent } from "../../../non-conformite/components/nc-aside-block/nc-aside-block.component";
import { NonConformiteFacadeService } from "../../../non-conformite/services/non-conformite/non-conformite-facade.service";

export interface VenteEvolutionLot {
  lotId: string | null;
  lotNumero: string;
  qtyUV: number;
  qtyUG: number;
  uniteVente: string;
  uniteGestion: string;
  puVente: number;
  puCout: number;
  marge: number;
  tauxMarge: number;
}

export interface VenteEvolutionFactureRow {
  factureId: string;
  numero: string;
  dateKey: string;
  clientDenomination: string;
  qtyUV: number;
  puVenteMoyen: number;
  puCoutMoyen: number;
  ca: number;
  cout: number;
  marge: number;
  tauxMarge: number;
  lots: VenteEvolutionLot[];
}

export interface VenteEvolutionDialogData {
  articleDenomination: string;
  articleUniteVente: string;
  tiersDenomination: string;
  tiersId?: string;
  articleId?: string;
  factures: VenteEvolutionFactureRow[];
}

type ViewMode = "byFacture" | "byMonth";

interface MonthRow {
  monthKey: string;
  monthLabel: string;
  nbFactures: number;
  qtyUV: number;
  ca: number;
  cout: number;
  marge: number;
  tauxMarge: number;
  puVenteMoyen: number;
}

interface Stats {
  puMoyen: number;
  puMin: number;
  puMax: number;
  variation: number;
  margeFormatted: string;
  tauxMarge: number;
  nbFactures: number;
  qtyTotale: number;
  caTotal: number;
}

@Component({
  selector: "vl-stats-vente-evolution-dialog",
  standalone: true,
  imports: [
    CommonModule, SharedModule, DialogHeaderExportComponent,
    DecimalPipe, MatIcon, MatTooltip,
    RadioCardComponent, LotCardComponent, ChipsToggleComponent,
    NcAsideBlockComponent,
  ],
  templateUrl: "./stats-vente-evolution-dialog.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatsVenteEvolutionDialogComponent {
  data = inject<VenteEvolutionDialogData>(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<StatsVenteEvolutionDialogComponent>);

  expanded = signal<Set<string>>(new Set());
  viewMode = signal<ViewMode>("byFacture");
  viewModeElements = [
    { name: "Par facture", value: "byFacture" as ViewMode },
    { name: "Par mois", value: "byMonth" as ViewMode },
  ];

  sortedFactures = computed<VenteEvolutionFactureRow[]>(() =>
    [...this.data.factures].sort((a, b) =>
      a.dateKey === b.dateKey
        ? a.numero.localeCompare(b.numero)
        : b.dateKey.localeCompare(a.dateKey),
    ),
  );

  monthRows = computed<MonthRow[]>(() => {
    const map = new Map<string, MonthRow>();
    const fmtFR = new Intl.DateTimeFormat("fr-FR", { year: "numeric", month: "long" });
    for (const f of this.data.factures) {
      const d = new Date(f.dateKey);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      if (!map.has(monthKey)) {
        let label = monthKey;
        try { label = fmtFR.format(d); } catch { /* ignore */ }
        map.set(monthKey, {
          monthKey, monthLabel: label,
          nbFactures: 0, qtyUV: 0, ca: 0, cout: 0, marge: 0, tauxMarge: 0, puVenteMoyen: 0,
        });
      }
      const m = map.get(monthKey)!;
      m.nbFactures++;
      m.qtyUV += f.qtyUV;
      m.ca += f.ca;
      m.cout += f.cout;
      m.marge += f.marge;
    }
    const rows = [...map.values()];
    for (const m of rows) {
      m.puVenteMoyen = m.qtyUV !== 0 ? m.ca / m.qtyUV : 0;
      m.tauxMarge = m.ca > 0 ? (m.marge / m.ca) * 100 : 0;
    }
    return rows.sort((a, b) => b.monthKey.localeCompare(a.monthKey));
  });

  stats = computed<Stats>(() => {
    const fs = this.data.factures;
    if (!fs.length) {
      return {
        puMoyen: 0, puMin: 0, puMax: 0, variation: 0,
        margeFormatted: "- €", tauxMarge: 0,
        nbFactures: 0, qtyTotale: 0, caTotal: 0,
      };
    }
    let qtyTotale = 0, caTotal = 0, coutTotal = 0;
    let puMin = Infinity, puMax = -Infinity;
    for (const f of fs) {
      qtyTotale += f.qtyUV;
      caTotal += f.ca;
      coutTotal += f.cout;
      if (f.qtyUV > 0) {
        if (f.puVenteMoyen < puMin) puMin = f.puVenteMoyen;
        if (f.puVenteMoyen > puMax) puMax = f.puVenteMoyen;
      }
    }
    const puMoyen = qtyTotale !== 0 ? caTotal / qtyTotale : 0;
    const margeTotal = caTotal - coutTotal;
    const tauxMarge = caTotal > 0 ? (margeTotal / caTotal) * 100 : 0;

    const chrono = [...fs].sort((a, b) => a.dateKey.localeCompare(b.dateKey));
    const first = chrono[0]?.puVenteMoyen ?? 0;
    const last = chrono.at(-1)?.puVenteMoyen ?? 0;
    const variation = first > 0 ? ((last - first) / first) * 100 : 0;

    return {
      puMoyen,
      puMin: Number.isFinite(puMin) ? puMin : 0,
      puMax: Number.isFinite(puMax) ? puMax : 0,
      variation,
      margeFormatted: WidgetHelperService.formatNumeric(margeTotal) + " €",
      tauxMarge,
      nbFactures: fs.length,
      qtyTotale,
      caTotal,
    };
  });

  margeColor = computed(() => {
    const t = this.stats().tauxMarge;
    if (t >= 35) return "valid";
    if (t >= 20) return "warn";
    return "error";
  });

  variationColor = computed(() => {
    const v = this.stats().variation;
    if (v > 0.5) return "valid";
    if (v < -0.5) return "error";
    return "secondary";
  });

  margeClass(taux: number): string {
    if (taux >= 35) return "on-valid";
    if (taux >= 20) return "on-warning";
    return "on-error";
  }

  toggle(key: string) {
    const current = new Set(this.expanded());
    current.has(key) ? current.delete(key) : current.add(key);
    this.expanded.set(current);
  }

  isOpen(key: string): boolean {
    return this.expanded().has(key);
  }

  formatDate(dateKey: string): string {
    try {
      return new Intl.DateTimeFormat("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" })
        .format(new Date(dateKey));
    } catch { return dateKey; }
  }

  exporting = signal(false);
  private ncFacade = inject(NonConformiteFacadeService);

  async exportExcel() {
    this.exporting.set(true);
    try {
      const ncs = await this.loadNcs();
      await exportVenteEvolutionExcel(this.data, ncs);
    } finally {
      this.exporting.set(false);
    }
  }

  /**
   * Charge les NC pour l'onglet Excel — intersection tiers ∩ article si
   * les deux sont fournis (cas dialog évolution vente d'un article pour
   * un client). Sinon par tiers ou par article seul.
   */
  private async loadNcs() {
    if (this.data.tiersId && this.data.articleId) {
      const all = await this.ncFacade.loadByTiers(this.data.tiersId);
      return (all ?? []).filter((nc) => nc.articleId === this.data.articleId);
    }
    if (this.data.tiersId) return this.ncFacade.loadByTiers(this.data.tiersId);
    if (this.data.articleId) return this.ncFacade.loadByArticle(this.data.articleId);
    return undefined;
  }
}
